import { useState } from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const answered = (i) => i.checked || i.na || i.pending;

const TriItem = ({ it, onToggle, disabled }) => {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(it.notes || "");
  const set = (patch) => onToggle(it, { checked: false, na: false, pending: false, ...patch });
  return (
    <div className={cn("space-y-2 rounded-lg border p-3", it.checked || it.na ? "border-emerald-500/30 bg-emerald-500/5" : it.pending ? "border-amber-500/40 bg-amber-500/5" : "border-border bg-card/50")} data-testid={`checklist-item-${it.id}`}>
      <div className="text-sm">{it.label}</div>
      <div className="grid grid-cols-3 gap-1">
        <Button size="sm" variant={it.checked ? "default" : "outline"} disabled={disabled} onClick={() => set({ checked: true })} data-testid={`checklist-item-${it.id}-done`}>Realizado</Button>
        <Button size="sm" variant={it.na ? "default" : "outline"} disabled={disabled} onClick={() => set({ na: true })} data-testid={`checklist-item-${it.id}-na`}>No aplica</Button>
        <Button size="sm" variant={it.pending ? "default" : "outline"} disabled={disabled} onClick={() => setOpen(true)} data-testid={`checklist-item-${it.id}-pending`}>Pendiente</Button>
      </div>
      {it.pending && !open && <p className="text-xs text-amber-300" data-testid={`checklist-item-${it.id}-justification`}>Pendiente: {it.notes}</p>}
      {open && !disabled && (
        <div className="space-y-2">
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Justificación del pendiente (obligatoria)" data-testid={`checklist-item-${it.id}-notes`} />
          <Button size="sm" className="w-full" disabled={notes.trim().length < 3} onClick={() => { set({ pending: true, notes }); setOpen(false); }} data-testid={`checklist-item-${it.id}-pending-save`}>Guardar pendiente</Button>
        </div>
      )}
    </div>
  );
};

const PlainItem = ({ it, onToggle, disabled, large }) => (
  <button type="button" disabled={disabled || !onToggle} onClick={() => onToggle?.(it)} data-testid={`checklist-item-${it.id}`}
    className={cn("flex w-full items-center gap-3 rounded-lg border px-3 text-left transition-colors disabled:cursor-default",
      large ? "min-h-[56px] py-3" : "py-2",
      it.checked ? "border-emerald-500/30 bg-emerald-500/5" : "border-border bg-card/50 enabled:hover:border-primary/40")}>
    <span className={cn("flex shrink-0 items-center justify-center rounded-md border-2 transition-colors", large ? "h-7 w-7" : "h-5 w-5",
      it.checked ? "border-emerald-500 bg-emerald-500 text-white" : "border-slate-600")}>
      {it.checked && <Check className={large ? "h-4 w-4" : "h-3 w-3"} strokeWidth={3} />}
    </span>
    <span className={cn("flex-1", large ? "text-base" : "text-sm", it.checked && "text-muted-foreground")}>{it.label}</span>
    {it.required && !it.checked && <span className="font-mono text-[10px] uppercase text-amber-400">Obligatorio</span>}
  </button>
);

export const ChecklistList = ({ items, onToggle, disabled, large, tri }) => {
  if (!items?.length) return <p className="text-sm text-muted-foreground">Esta OS no tiene checklist configurado.</p>;
  const done = items.filter((i) => (tri ? answered(i) : i.checked)).length;
  return (
    <div className="space-y-2" data-testid="checklist-list">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span className="font-mono uppercase tracking-wider">{tri ? "Respondidas" : "Progreso"}</span>
        <span className="font-mono" data-testid="checklist-progress">{done}/{items.length}</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-secondary">
        <div className="h-full bg-primary transition-[width] duration-300" style={{ width: `${(done / items.length) * 100}%` }} />
      </div>
      <div className="space-y-1.5 pt-2">
        {items.map((it, n) => (
          <div key={it.id} className="space-y-1.5">
            {it.stage && it.stage !== items[n - 1]?.stage && <div className="pt-2 font-mono text-[11px] uppercase tracking-[0.15em] text-primary" data-testid={`checklist-stage-${n}`}>{it.stage}</div>}
            {tri ? <TriItem it={it} onToggle={onToggle} disabled={disabled || !onToggle} /> : <PlainItem it={it} onToggle={onToggle} disabled={disabled} large={large} />}
          </div>
        ))}
      </div>
    </div>
  );
};
